import { FC, useEffect, useState } from 'react';
import styles from './WizardResultForInfraSeller.module.scss';
import { forkJoin, of } from 'rxjs';
import { RxUtils, Tr } from '@utils';
import { useService } from '@hooks';
import { PortalRoutePath } from '@models';
import {
	ChargerCurrentType,
	ChargerProperties,
	Product,
	ProductCategoryType,
	productService,
	QuotationItem,
	quotationService
} from '@services';
import { RouterLinkButton } from '@components/common';
import { LocationChargerImage } from '@components/portal/locations';
import { Box, Grid, Stack, Typography } from '@mui/material';

const isOfficeCharger = (item: QuotationItem): boolean => {
	return (
		item.productCategory === ProductCategoryType.CHARGING_POINT &&
		(item.product.properties as ChargerProperties).currentType === ChargerCurrentType.AC &&
		!!item.checkout
	);
};
const isHomeCharger = (item: QuotationItem): boolean => {
	// TODO: [VOL-707] ChargerCurrentType does not define home and office chargers
	return (
		item.productCategory === ProductCategoryType.CHARGING_POINT &&
		(item.product.properties as ChargerProperties).currentType === ChargerCurrentType.DC &&
		!!item.checkout
	);
};

const getTotalCountsFromQuotationItems = (items: QuotationItem[]): number => {
	return items.reduce((acc, item) => acc + item.numberOfItems, 0);
};

const getUniqueProductsFromQuotationItems = (items: QuotationItem[]): Product[] => {
	return items.reduce((acc, item) => {
		if (!acc.find((alreadyAddedItem) => alreadyAddedItem.id === item.product.id)) {
			return [...acc, item.product];
		}
		return acc;
	}, [] as Product[]);
};

export const WizardResultForInfraSeller: FC = () => {
	const [buttonVisible, setButtonVisible] = useState(false);
	const [officeChargers, setOfficeChargers] = useState<QuotationItem[]>([]);
	const [homeChargers, setHomeChargers] = useState<QuotationItem[]>([]);
	const [officeChargerCount, setOfficeChargerCount] = useState(0);
	const [homeChargerCount, setHomeChargerCount] = useState(0);
	const [officeChargerImages, setOfficeChargerImages] = useState<string[]>([]);
	const [homeChargerImages, setHomeChargerImages] = useState<string[]>([]);
	const { data: latestQuotation } = useService(() => quotationService.getLatest());
	const { data: quotationItems = [] } = useService(
		() => (latestQuotation?.id ? quotationService.quotationItem(latestQuotation.id).getItems() : of()),
		[latestQuotation]
	);

	useEffect(() => {
		const timer = setTimeout(() => setButtonVisible(true), 4000);
		return () => clearTimeout(timer);
	});

	useEffect(() => {
		const officeChargersLocal = quotationItems.filter(isOfficeCharger);
		const homeChargersLocal = quotationItems.filter(isHomeCharger);
		const officeChargerProducts = getUniqueProductsFromQuotationItems(officeChargersLocal);
		const homeChargerProducts = getUniqueProductsFromQuotationItems(homeChargersLocal);

		setOfficeChargers(officeChargersLocal);
		setHomeChargers(homeChargersLocal);

		setOfficeChargerCount(getTotalCountsFromQuotationItems(officeChargersLocal));
		setHomeChargerCount(getTotalCountsFromQuotationItems(homeChargersLocal));

		RxUtils.promisify(forkJoin(officeChargerProducts.map((item) => productService.getImages(item))), (results) => {
			setOfficeChargerImages(results.flat());
		});
		RxUtils.promisify(forkJoin(homeChargerProducts.map((item) => productService.getImages(item))), (results) => {
			setHomeChargerImages(results.flat());
		});
	}, [quotationItems]);

	return (
		<Box className={styles.WizardResultForInfraSeller}>
			<Grid container className={styles.BgContainer}>
				<Grid item xs={6} className={styles.Left}></Grid>
				<Grid item xs={6} className={styles.Right}></Grid>
			</Grid>
			<Box className={styles.Overlay} sx={{ backgroundColor: 'primary.main' }} />

			<Stack direction="column" className={styles.ContentContainer} sx={{ color: 'common.white', paddingTop: 8, paddingBottom: 10 }}>
				<Stack direction="column" alignItems="center">
					<Typography variant="h2" sx={{ fontWeight: 600 }}>
						<Tr.Portal path="wizard.result.infra-seller.title-line1" />
					</Typography>
					<Typography variant="h6">
						<Tr.Portal path="wizard.result.infra-seller.title-line2" />
					</Typography>
				</Stack>

				<Grid container>
					<Grid item xs={6} display="flex" justifyContent="center">
						<Stack direction="row" alignItems="end" spacing={2}>
							<LocationChargerImage type="image" url={officeChargerImages[0]} width={180} circleSize={160} />
							<Stack direction="column">
								<Typography variant="h2" sx={{ fontWeight: 800, fontSize: 56 }}>
									{officeChargerCount}
								</Typography>
								<Typography variant="h4">
									<Tr.Portal path="wizard.result.infra-seller.office-chargers-title" />
								</Typography>
								<Typography variant="body1">
									<Tr.Portal path="wizard.result.infra-seller.office-chargers-subtitle" />
								</Typography>
							</Stack>
						</Stack>
					</Grid>
					<Grid item xs={6} display="flex" justifyContent="center">
						<Stack direction="row" alignItems="end" spacing={2}>
							<LocationChargerImage type="image" url={homeChargerImages[0]} width={180} circleSize={160} />
							<Stack direction="column">
								<Typography variant="h2" sx={{ fontWeight: 800, fontSize: 56 }}>
									{homeChargerCount}
								</Typography>
								<Typography variant="h4">
									<Tr.Portal path="wizard.result.infra-seller.home-chargers-title" />
								</Typography>
								<Typography variant="body1">
									<Tr.Portal path="wizard.result.infra-seller.home-chargers-subtitle" />
								</Typography>
							</Stack>
						</Stack>
					</Grid>
				</Grid>

				<Stack direction="column" alignItems="center" spacing={1}>
					<Typography variant="h6">
						<Tr.Portal path="wizard.result.infra-seller.bottom-line1" />
					</Typography>
					<RouterLinkButton
						variant="contained"
						disabled={!buttonVisible}
						color="warning"
						sx={{ marginTop: '32px !important' }}
						href={`/portal/${PortalRoutePath.QUOTATION}`}
					>
						<Tr.Portal path="wizard.result.infra-seller.bottom-navigation" />
					</RouterLinkButton>
				</Stack>
			</Stack>
		</Box>
	);
};
